import { msg, t } from "@lingui/core/macro";
import { Price } from "@silversea-ssc/ui-components-react";
import { fareByPriority } from "features/fare/fares";
import { formatPrice, getWaitlistLabel } from "src/utils/formatPrice";
import { Currency } from "src/utils/localization/currency";
import { match, P } from "ts-pattern";
import { formatI18nCurrency } from "src/utils/localization/formatCurrency";
import { isPresent } from "src/utils/checks";
import { AvailableWebPrice } from "@silversea-ssc/dotcom-api-types/Prices";
import {
  CruisePriceAndOffersV2Response,
  SuitePriceV2
} from "@silversea-ssc/dotcom-api-types/CruisePriceAndOffersV2";
import { EnhancedAvailablePriceWithFareCode } from "src/ssc-api/prices/types";
import {
  LinguiT,
  translationOrString
} from "src/utils/localization/lingui-helpers";
import { priceForSuite } from "src/utils/suites";
import { FareCode } from "@silversea-ssc/web-fares";

type PriceFromLabelFormat = "without-from" | "only-from" | "none";

// TODO: check if all the formats are used
// TODO: try to remove as we don't need this flexibility anymore (mainly we use only "with lowest fare available")
export const transformPriceFrom = (
  t: LinguiT,
  priceFrom: AvailableWebPrice | undefined,
  currency: Currency | undefined,
  showOriginalDiscountedPrice: boolean,
  labelFormat: PriceFromLabelFormat,
  fareLabelLookup: (fareCode: FareCode) => string
): Price | undefined => {
  if (!isPresent(priceFrom)) return undefined;

  return match<[AvailableWebPrice, boolean], Price | undefined>([
    priceFrom,
    showOriginalDiscountedPrice
  ])
    .with([P.nullish, P.any], () => undefined)
    .with([{ type: "waitlist" }, P.boolean], () => ({
      type: "waitlist",
      fareLabel: translationOrString(
        t,
        transformLabelFormat(priceFrom, labelFormat, fareLabelLookup)
      ),
      waitlistLabel: getWaitlistLabel()
    }))
    .with([{ type: "discounted" }, true], ([priceFrom]) => ({
      type: "discounted",
      fareLabel: translationOrString(
        t,
        transformLabelFormat(priceFrom, labelFormat, fareLabelLookup)
      ),
      formattedAmount: formatI18nCurrency(priceFrom.amount, currency) || " ",
      formattedOriginalAmount:
        formatI18nCurrency(priceFrom.originalAmount, currency) || " "
    }))
    .with(
      [{ type: "price" }, P.boolean],
      [{ type: "discounted" }, false],
      ([priceFrom]) => ({
        type: "price",
        fareLabel: translationOrString(
          t,
          transformLabelFormat(priceFrom, labelFormat, fareLabelLookup)
        ),
        formattedAmount: formatPrice(priceFrom, currency) || " "
      })
    )
    .exhaustive();
};

const transformLabelFormat = (
  price: EnhancedAvailablePriceWithFareCode,
  labelFormat: PriceFromLabelFormat,
  fareLabelLookup: (fareCode: FareCode) => string
) => {
  const fareName = fareLabelLookup(price.webFareCode);

  return match(labelFormat)
    .with("none", () => "")
    .with("only-from", () => fromPiceLabel())
    .with("without-from", () => fareLabel(fareName))
    .exhaustive();
};

export const suitePricestoPrices = (
  t: LinguiT,
  fareLabelLookup: (fareCode: FareCode) => string,
  suiteCategotyCodes: string[],
  price: readonly SuitePriceV2[],
  currency: Currency | undefined,
  showOriginalDiscountedPrice = false,
  labelFormat?: PriceFromLabelFormat
):
  | { prices: Price[]; suiteCategory: string; originalSuitePrice: SuitePriceV2 }
  | undefined => {
  const suitePrices = priceForSuite(price, suiteCategotyCodes);
  if (!isPresent(suitePrices) || !suitePrices.prices) return undefined;
  const transformedPrces = [...suitePrices.prices]
    .sort((a, b) => fareByPriority(a.fare, b.fare))
    .map((p) =>
      transformPriceFrom(
        t,
        p.price,
        currency,
        showOriginalDiscountedPrice,
        labelFormat || "without-from",
        fareLabelLookup
      )
    )
    .filter(isPresent);

  return {
    prices: transformedPrces,
    suiteCategory: suitePrices.suiteCategory,
    originalSuitePrice: suitePrices
  };
};

export const cruisePriceToPrices = (
  t: LinguiT,
  prices: CruisePriceAndOffersV2Response | undefined,
  currency: Currency | undefined,
  fareLabelLookup: (fareCode: FareCode) => string
) => [
  transformPriceFrom(
    t,
    prices?.priceFrom,
    currency,
    true,
    "none",
    fareLabelLookup
  )
];

export const fromPiceLabel = () => msg({ id: "from-price" });
export const withLowestFareAvailableLabel = () =>
  msg`with lowest fare available`;

export const fareLabel = (fareName: string) => t`Per guest, with ${fareName}`;

export const perGuestLabel = () => msg`Per guest`;
export const isPriceWaitlist = <T extends { type: AvailableWebPrice["type"] }>(
  priceFrom: T | undefined
) => priceFrom?.type == "waitlist";
